import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BarChart3, Download, RefreshCw } from 'lucide-react';
import { getInventoryReport, getInventorySettings, listRestaurants } from '../services/api';
import HelpIcon from '../components/help/HelpIcon';
import useFeatureGate from '../hooks/useFeatureGate';
import { FEATURE_GATE_ERROR_CODE } from '../config/featureGates';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import { downloadCsv } from '../utils/kardex';
import { storage } from '../utils/storage';
import {
  ALL_RESTAURANTS,
  GROUP_BY_OPTIONS,
  REPORT_TABS,
  activeRestaurants,
  canViewAllRestaurants,
  defaultRange,
  intlLocale,
  isEmptyReport,
  reportCsvFileName,
  reportCsvRows,
  reportParams,
  restaurantName,
  rowsToCsv,
  validateRange,
} from '../utils/inventoryReports';
import useChartColors from '../components/inventory-reports/useChartColors';
import ConsumptionReport from '../components/inventory-reports/ConsumptionReport';
import CogsReport from '../components/inventory-reports/CogsReport';
import VarianceReport from '../components/inventory-reports/VarianceReport';
import WasteReport from '../components/inventory-reports/WasteReport';
import './InventoryReportsPage.css';

const VIEWS = {
  consumption: ConsumptionReport,
  cogs: CogsReport,
  variance: VarianceReport,
  waste: WasteReport,
};

const isFeatureGateError = (error) =>
  error?.response?.status === 403 && error?.response?.data?.error === FEATURE_GATE_ERROR_CODE;

/**
 * RES-47 (épica D5) — Reportes de inventario por local y de la cadena: consumo, costo de venta,
 * varianza y mermas. Rango de fechas (últimos 30 días por defecto, máximo 366), local (o todos,
 * si el usuario administra la organización y el plan tiene multi-local), agrupación del COGS y
 * export CSV de la vista actual. Todo sale del kardex, así que los números cierran contra
 * Movimientos.
 */
function InventoryReportsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const locale = intlLocale(i18n?.language);
  const rootRef = useRef(null);
  const colors = useChartColors(rootRef);

  const tabParam = searchParams.get('view');
  const tab = REPORT_TABS.includes(tabParam) ? tabParam : 'consumption';
  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', next);
    setSearchParams(params, { replace: true });
  };

  const [range, setRange] = useState(() => defaultRange());
  const [restaurantId, setRestaurantId] = useState(() => storage.get('selectedRestaurantId') || '');
  const [groupBy, setGroupBy] = useState('day');
  const [includeUncounted, setIncludeUncounted] = useState(false);

  const [restaurants, setRestaurants] = useState([]);
  const [consolidatedRead, setConsolidatedRead] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [gated, setGated] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const advancedGate = useFeatureGate('advancedReportsEnabled');
  const recipesGate = useFeatureGate('recipesEnabled');
  const multiGate = useFeatureGate('multiRestaurantEnabled');
  const planBlocked =
    (!advancedGate.loading && !advancedGate.enabled) ||
    (!recipesGate.loading && !recipesGate.enabled);
  const blockingGate = !advancedGate.enabled ? advancedGate : recipesGate;

  useEffect(() => {
    let cancelled = false;
    Promise.all([listRestaurants().catch(() => []), getInventorySettings().catch(() => null)]).then(
      ([restaurantsData, settings]) => {
        if (cancelled) return;
        setRestaurants(activeRestaurants(restaurantsData));
        setConsolidatedRead(settings?.can?.consolidatedRead === true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const allowAll = canViewAllRestaurants({
    consolidatedRead,
    multiRestaurant: multiGate.enabled === true,
    restaurantCount: restaurants.length,
  });

  // Si se pierde el permiso (o todavía no se sabe) no se queda pidiendo el consolidado
  const effectiveRestaurantId =
    restaurantId === ALL_RESTAURANTS && !allowAll
      ? storage.get('selectedRestaurantId') || ''
      : restaurantId;

  const rangeError = validateRange(range);
  const params = useMemo(
    () =>
      reportParams(tab, {
        from: range.from,
        to: range.to,
        restaurantId: effectiveRestaurantId,
        groupBy,
        includeUncounted,
      }),
    [tab, range.from, range.to, effectiveRestaurantId, groupBy, includeUncounted]
  );
  const paramsKey = JSON.stringify(params);

  // Solo se aplica la respuesta de la última consulta (los cambios de filtro se pisan)
  const requestSeq = useRef(0);
  useEffect(() => {
    if (planBlocked || rangeError) {
      requestSeq.current += 1;
      setData(null);
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');
    setGated(false);
    getInventoryReport(tab, JSON.parse(paramsKey))
      .then((response) => {
        if (seq !== requestSeq.current) return;
        setData(response);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        console.error('Error loading inventory report:', err);
        setData(null);
        // El interceptor global ya abrió el modal de upgrade: acá solo un estado neutro
        if (isFeatureGateError(err)) setGated(true);
        else setError(inventoryErrorMessage(t, err, 'inventory.reports.states.loadError'));
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [tab, paramsKey, planBlocked, rangeError, reloadKey, t]);

  const nameOf = useCallback(
    (id) => restaurantName(id, data?.restaurants, restaurants),
    [data, restaurants]
  );
  const reasonLabel = useCallback(
    (code) => t(`inventory.movementsPage.wasteReasons.${code}`, { defaultValue: code }),
    [t]
  );

  // La respuesta puede ser de la vista anterior mientras carga la nueva
  const current = data && data.report === tab ? data : null;
  const empty = Boolean(current) && isEmptyReport(tab, current);

  const handleExport = () => {
    if (!current) return;
    const rows = reportCsvRows(tab, current, {
      label: (key) => t(`inventory.reports.csv.${key}`),
      restaurantName: nameOf,
      reasonLabel,
    });
    downloadCsv(rowsToCsv(rows), reportCsvFileName(tab, current, effectiveRestaurantId));
  };

  const setRangeField = (field) => (event) => {
    const { value } = event.target;
    setRange((prev) => ({ ...prev, [field]: value }));
  };

  const View = VIEWS[tab];
  const showRestaurantSelect = restaurants.length > 1;

  const renderBody = () => {
    if (planBlocked || gated) {
      return (
        <div className="empty-state report-state" data-state="gated">
          <BarChart3 size={40} aria-hidden="true" />
          <p>{t('inventory.reports.states.gated')}</p>
          {planBlocked && (
            <button type="button" className="btn btn-primary" onClick={blockingGate.open}>
              {t('inventory.reports.actions.seePlans')}
            </button>
          )}
        </div>
      );
    }
    if (rangeError) {
      return (
        <div className="alert alert-error" role="alert" data-state="range-error">
          {t(`inventory.errors.${rangeError}`)}
        </div>
      );
    }
    if (error) {
      return (
        <div className="alert alert-error report-state-error" role="alert" data-state="error">
          <span>{error}</span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setReloadKey((k) => k + 1)}
          >
            <RefreshCw size={14} />
            {t('inventory.reports.actions.retry')}
          </button>
        </div>
      );
    }
    if (loading || !current) {
      return (
        <div className="loading" data-state="loading">
          {t('inventory.reports.states.loading')}
        </div>
      );
    }
    if (empty) {
      return (
        <div className="empty-state report-state" data-state="empty">
          <BarChart3 size={40} aria-hidden="true" />
          <p>{t(`inventory.reports.states.empty.${tab}`)}</p>
          <p className="report-hint">{t('inventory.reports.states.emptyHint')}</p>
        </div>
      );
    }
    return (
      <View
        data={current}
        t={t}
        locale={locale}
        colors={colors}
        nameOf={nameOf}
        reasonLabel={reasonLabel}
        includeUncounted={includeUncounted}
        onIncludeUncounted={setIncludeUncounted}
      />
    );
  };

  return (
    <div className="inventory-reports-page" ref={rootRef}>
      <header className="report-header">
        <button
          type="button"
          className="btn btn-ghost btn-sm report-back"
          onClick={() => navigate('/inventory')}
        >
          <ArrowLeft size={16} />
          {t('inventory.reports.back')}
        </button>
        <div className="report-title-row">
          <h1>{t('inventory.reports.title')}</h1>
          <HelpIcon topicKey="inventory.reports" />
        </div>
        <p className="report-subtitle">{t('inventory.reports.subtitle')}</p>
      </header>

      <div className="report-tabs" role="tablist">
        {REPORT_TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`report-tab${tab === key ? ' is-active' : ''}`}
            onClick={() => setTab(key)}
            data-tab={key}
          >
            {t(`inventory.reports.tabs.${key}`)}
          </button>
        ))}
      </div>

      <div className="report-filters">
        <label className="report-field">
          <span>{t('inventory.reports.filters.from')}</span>
          <input
            type="date"
            value={range.from}
            max={range.to || undefined}
            onChange={setRangeField('from')}
            data-testid="report-from"
          />
        </label>
        <label className="report-field">
          <span>{t('inventory.reports.filters.to')}</span>
          <input
            type="date"
            value={range.to}
            min={range.from || undefined}
            onChange={setRangeField('to')}
            data-testid="report-to"
          />
        </label>
        {showRestaurantSelect && (
          <label className="report-field">
            <span>{t('inventory.reports.filters.restaurant')}</span>
            <select
              value={effectiveRestaurantId}
              onChange={(e) => setRestaurantId(e.target.value)}
              data-testid="report-restaurant"
            >
              {allowAll && (
                <option value={ALL_RESTAURANTS}>
                  {t('inventory.reports.filters.allRestaurants')}
                </option>
              )}
              {restaurants.map((r) => (
                <option key={r.restaurantId} value={r.restaurantId}>
                  {r.name || r.restaurantId}
                </option>
              ))}
            </select>
          </label>
        )}
        {tab === 'cogs' && (
          <label className="report-field">
            <span>{t('inventory.reports.filters.groupBy')}</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              data-testid="report-group-by"
            >
              {GROUP_BY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`inventory.reports.groupBy.${option}`)}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="report-filters-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleExport}
            disabled={!current || empty || loading}
            data-testid="report-export"
          >
            <Download size={16} />
            {t('inventory.reports.actions.exportCsv')}
          </button>
        </div>
      </div>

      {current && !loading && (
        <p className="report-meta">
          {t('inventory.reports.meta', {
            from: current.from,
            to: current.to,
            count: Number(current.movementCount) || 0,
          })}
          {current.consolidated && (
            <span className="report-tag">
              {t('inventory.reports.consolidatedTag', {
                count: (current.restaurants || []).length,
              })}
            </span>
          )}
        </p>
      )}

      {renderBody()}
    </div>
  );
}

export default InventoryReportsPage;
